import { useState, useEffect, useCallback } from 'react';
import { usersApi, User, UpdateUserDto, UserDetails } from '../../../api/users';
import { toast } from 'sonner';
import { UseUsersReturn } from '../types';

const ITEMS_PER_PAGE = 10;

export function useUsers(): UseUsersReturn {
  const [users, setUsers] = useState<User[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>('all');
  // 'false' — активные (по умолчанию), 'true' — удалённые, 'all' — все
  const [deletionFilter, setDeletionFilter] = useState<string>('false');
  const [isLoading, setIsLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<UpdateUserDto>({});
  const [viewingUser, setViewingUser] = useState<UserDetails | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [restoringUser, setRestoringUser] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      if (roleFilter !== 'all') {
        params.role = roleFilter;
      }

      if (subscriptionFilter !== 'all') {
        params.isSubscribed = subscriptionFilter;
      }

      params.isDeleted = deletionFilter;

      const response = await usersApi.getUsers(params);
      setUsers(response.users);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось загрузить пользователей');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery, roleFilter, subscriptionFilter, deletionFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleRoleFilter = (value: string) => {
    setRoleFilter(value);
    setCurrentPage(1);
  };

  const handleSubscriptionFilter = (value: string) => {
    setSubscriptionFilter(value);
    setCurrentPage(1);
  };

  const handleDeletionFilter = (value: string) => {
    setDeletionFilter(value);
    setCurrentPage(1);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setEditFormData({
      email: user.email || '',
      username: user.username,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role,
      isSubscribed: user.isSubscribed,
      // <input type="datetime-local"> не понимает ISO с зоной — режем до минут.
      subscribedEnd: user.subscribedEnd
        ? new Date(user.subscribedEnd).toISOString().slice(0, 16)
        : null,
      isBlocked: user.isBlocked,
    });
    setIsEditDialogOpen(true);
  };

  const handleView = async (user: User) => {
    setIsViewDialogOpen(true);
    setIsLoadingDetails(true);
    try {
      const details = await usersApi.getUserDetails(user.id);
      setViewingUser(details);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось загрузить данные пользователя');
      setIsViewDialogOpen(false);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleSave = async () => {
    if (!editingUser || isSaving) return;

    // Премиум без даты окончания не виден приложению — не даём его выдать.
    if (editFormData.isSubscribed && !editFormData.subscribedEnd) {
      toast.error('Укажите, до какой даты действует подписка');
      return;
    }

    setIsSaving(true);
    try {
      await usersApi.updateUser(editingUser.id, {
        ...editFormData,
        subscribedEnd: editFormData.subscribedEnd
          ? new Date(editFormData.subscribedEnd).toISOString()
          : null,
      });
      toast.success('Пользователь обновлён');
      setIsEditDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось обновить пользователя');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingUser || isDeleting) return;

    setIsDeleting(true);
    try {
      await usersApi.deleteUser(deletingUser.id);
      toast.success('Аккаунт удалён');
      setDeletingUser(null);
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось удалить аккаунт');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestore = async () => {
    if (!restoringUser || isRestoring) return;

    setIsRestoring(true);
    try {
      await usersApi.restoreUser(restoringUser.id);
      toast.success('Аккаунт восстановлен');
      setRestoringUser(null);
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось восстановить аккаунт');
    } finally {
      setIsRestoring(false);
    }
  };

  return {
    users,
    total,
    totalPages,
    currentPage,
    isLoading,
    searchQuery,
    roleFilter,
    subscriptionFilter,
    deletionFilter,
    editingUser,
    isEditDialogOpen,
    editFormData,
    viewingUser,
    isViewDialogOpen,
    isLoadingDetails,
    isSaving,
    deletingUser,
    restoringUser,
    isDeleting,
    isRestoring,
    setCurrentPage,
    handleSearch,
    handleRoleFilter,
    handleSubscriptionFilter,
    handleDeletionFilter,
    handleEdit,
    handleView,
    handleSave,
    handleDelete,
    handleRestore,
    setDeletingUser,
    setRestoringUser,
    setIsEditDialogOpen,
    setIsViewDialogOpen,
    setEditFormData,
  };
}

export { ITEMS_PER_PAGE };
